import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const AddPartnerModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    industry: '',
    taxId: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
  });
  const [errors, setErrors] = useState({});
  const [isAdding, setIsAdding] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.companyName?.trim()) newErrors.companyName = 'Company name is required';
    if (!formData?.contactName?.trim()) newErrors.contactName = 'Contact name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.industry) newErrors.industry = 'Industry is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsAdding(true);
    
    setTimeout(() => {
      onAdd(formData);
      setIsAdding(false);
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        industry: '',
        taxId: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        website: '',
      });
      onClose();
    }, 1000);
  };

  if (!isOpen) return null;

  const industryOptions = [
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'financial-services', label: 'Financial Services' },
    { value: 'technology', label: 'Technology' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg border border-border elevation-3 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Add New Partner</h2>
              <p className="text-sm text-muted-foreground">Create a new partner account manually</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Building" size={18} />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  type="text"
                  name="companyName"
                  value={formData?.companyName}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  error={errors?.companyName}
                  required
                />
                <Select
                  label="Industry"
                  options={industryOptions}
                  value={formData?.industry}
                  onChange={(value) => handleSelectChange('industry', value)}
                  placeholder="Select industry"
                  error={errors?.industry}
                  required
                />
                <Input
                  label="Tax ID"
                  type="text"
                  name="taxId"
                  value={formData?.taxId}
                  onChange={handleChange}
                  placeholder="XX-XXXXXXX"
                  description="Optional - Federal Tax ID"
                />
                <Input
                  label="Website"
                  type="url"
                  name="website"
                  value={formData?.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  description="Optional - Company website"
                />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="User" size={18} />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Name"
                  type="text"
                  name="contactName"
                  value={formData?.contactName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  error={errors?.contactName}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="contact@example.com"
                  error={errors?.email}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  name="phone"
                  value={formData?.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  error={errors?.phone}
                  required
                />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="MapPin" size={18} />
                Address Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Street Address"
                  type="text"
                  name="address"
                  value={formData?.address}
                  onChange={handleChange}
                  placeholder="123 Business St, Suite 100"
                  description="Optional - Physical address"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="City"
                    type="text"
                    name="city"
                    value={formData?.city}
                    onChange={handleChange}
                    placeholder="Honolulu"
                  />
                  <Input
                    label="State"
                    type="text"
                    name="state"
                    value={formData?.state}
                    onChange={handleChange}
                    placeholder="HI"
                  />
                  <Input
                    label="ZIP Code"
                    type="text"
                    name="zipCode"
                    value={formData?.zipCode}
                    onChange={handleChange}
                    placeholder="96813"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isAdding}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            loading={isAdding}
            iconName="Check"
            iconPosition="left"
            onClick={handleSubmit}
          >
            {isAdding ? 'Creating Partner...' : 'Create Partner'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddPartnerModal;